import type { PositionPlanPositioningProps } from "./position-plan-positioning.type";
import type { Int } from "@/types";
import type { PdfViewerClickCoordinates, PdfViewerSize } from "@/ui/pdf-viewer";

import { useEffect, useState } from "react";

import {
  convertPdfClickCoordinatesToPositionCoordinates,
  convertPositionCoordinatesToMarkerCoordinates,
  getPlanPositioningCoordinatesPage,
} from "./position-plan-positioning.helper";

export const usePositionPlanPositioning = ({
  defaultCoordinates,
  controlledCoordinates,
  controlledPage,
  isEditable,
  onCoordinatesChange,
  onPageChange,
}: PositionPlanPositioningProps) => {
  const initialCoordinates = controlledCoordinates ?? defaultCoordinates;
  const [coordinates, setCoordinates] = useState(initialCoordinates);

  const markerPage = getPlanPositioningCoordinatesPage(coordinates);
  const initialPage = controlledPage ?? markerPage ?? 1;

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pdfSize, setPdfSize] = useState<PdfViewerSize>();

  const marker = convertPositionCoordinatesToMarkerCoordinates(
    coordinates,
    pdfSize
  );
  const showMarker = currentPage === markerPage;
  const isPageControlled = typeof controlledPage !== "undefined";

  const handlePdfClick = (clickCoordinates: PdfViewerClickCoordinates) => {
    if (!pdfSize) return;

    const newCoordinates = convertPdfClickCoordinatesToPositionCoordinates(
      clickCoordinates,
      pdfSize
    );

    setCoordinates(newCoordinates);
    onCoordinatesChange?.(newCoordinates);
  };

  const onCurrentPageChange = (page: Int) => {
    setCurrentPage(page);
    onPageChange?.(page);
  };

  useEffect(() => {
    if (typeof controlledCoordinates === "undefined") return;
    if (controlledCoordinates === coordinates) return;
    setCoordinates(controlledCoordinates ?? undefined);
  }, [controlledCoordinates, coordinates]);

  useEffect(() => {
    if (!controlledPage) return;
    if (controlledPage === currentPage) return;
    setCurrentPage(controlledPage);
  }, [controlledPage, currentPage]);

  return {
    markerPage,
    initialPage: currentPage,
    controlledPage: isPageControlled ? currentPage : undefined,
    marker: showMarker ? marker : undefined,
    onPageChange: onCurrentPageChange,
    setPdfSize,
    onPdfClick: isEditable ? handlePdfClick : undefined,
  };
};
